import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  Box,
  useColorMode,
  useColorStyle,
  Textarea,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useOrgStore } from '@Models/useOrgModel';
import { UserApi } from '@Apis';
import { ValidProps } from 'src/types';
import { validateEmail } from 'src/utils/validate';
import CSpinner from '@Components/CSpinner';
import EditableList from '@Components/EditableList';

export default function AdvancedOptionsModal(props: {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  changeRecipientDisclaimer: string;
  changeRecipientEmailAddressList: string;
  setChangeRecipientEmailAddressList: (value: string) => void;
  setChangeRecipientDisclaimer: (value: string) => void;
}) {
  const { t } = useTranslation();
  const { visible, onClose, onConfirm } = props;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = React.useState(false);
  const onAdd = (email: string) => {
    if (!props.changeRecipientEmailAddressList) {
      props.setChangeRecipientEmailAddressList(email);
      return;
    } else {
      const list = Array.from(
        new Set(props.changeRecipientEmailAddressList.split(',').concat(email))
      );
      props.setChangeRecipientEmailAddressList(list.join(','));
    }
  };

  const { orgId } = useOrgStore();

  const onDelete = (email: string) => {
    if (!props.changeRecipientEmailAddressList) {
      return;
    }
    const list = props.changeRecipientEmailAddressList
      .split(',')
      .filter((item) => item !== email);
    props.setChangeRecipientEmailAddressList(list.join(','));
  };

  const checkUser = async (value: string) => {
    try {
      setLoading(true);
      const res: boolean = (await UserApi.checkUserExist(orgId, value)).data;
      return res;
    } catch (e) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {}, []);

  const validates: ValidProps[] = [
    {
      validMethod: validateEmail,
      errMsg: t('common.error.invalidEmailAddress'),
    },
    {
      validMethod: checkUser,
      errMsg: t('common.error.invalidEmailAddress'),
    },
  ];

  return (
    <Box>
      {loading && <CSpinner />}
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={visible}
        onClose={onClose}
        size="md"
      >
        <ModalOverlay opacity={0.9} />
        <ModalContent width={480}>
          <ModalHeader></ModalHeader>
          <ModalBody>
            <Flex direction="column">
              <Box mb={24}>
                <Text mb={12}>
                  {t(
                    'policy.dlp.advanceOptions.changeRecipientActionSetting.title'
                  )}
                </Text>
                <Text mb={12} color={colorStyle.color.secondary}>
                  {t(
                    'policy.dlp.advanceOptions.changeRecipientActionSetting.content'
                  )}
                </Text>
                <Flex alignItems="center">
                  <Flex direction="column">
                    <EditableList
                      onDelete={onDelete}
                      onAdd={onAdd}
                      list={
                        props.changeRecipientEmailAddressList
                          ? props.changeRecipientEmailAddressList
                              .split(',')
                              .map((item) => ({ name: item, value: item }))
                          : []
                      }
                      disabled={false}
                      limit={5}
                      description={''}
                      width={'400px'}
                      size={4}
                      validates={validates}
                      placeholder={t(
                        'policy.dlp.advanceOptions.changeRecipientActionSetting.content.placeholder'
                      )}
                    />
                  </Flex>
                </Flex>
              </Box>
              <Text mb={12}>
                {t(
                  'policy.dlp.advanceOptions.DisclaimerInRedirectedEmail.title'
                )}
              </Text>
              <Text mb={12} color={colorStyle.color.secondary}>
                {t(
                  'policy.dlp.advanceOptions.DisclaimerInRedirectedEmail.content'
                )}
              </Text>
              <Textarea
                width={400}
                height={86}
                maxLength={1024}
                value={props.changeRecipientDisclaimer}
                onChange={(e: any) => {
                  props.setChangeRecipientDisclaimer(e.target.value);
                }}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Space width="2x" />
            <Button minWidth="20x" onClick={onConfirm}>
              {t('common.button.ok')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
